/**
 * A column mapping as a file the person keeps.
 *
 * It used to be kept on the account: named in a box on the screen, stored
 * against the company, and offered back to whoever next imported a file of
 * the same shape. That is a real feature and it was invisible - the button
 * said "Save", asked for a name, and never said where any of it went, so the
 * reasonable reading was that a file had been written somewhere and lost. The
 * user's ruling of 2026-09-02 is that a thing you cannot see is worse than a
 * thing you have to carry: a file, in a folder you chose, that you can send
 * to somebody.
 *
 * The names inside are the ones the dialog, the service and the server all
 * use - `fields` and `constants` - because a third vocabulary for the same
 * two things is how the halves of this feature drift apart.
 */
import { TransferSchema, TransferTable } from '../../services/table-import-export.service';

/** What the JSON says it is, so a file that is not one of these can say so
 *  rather than half-loading. */
export const MAPPING_FILE_KIND = 'bestroute.table-import-mapping';

export interface MappingFile {
  kind: typeof MAPPING_FILE_KIND;
  version: 1;
  /** Which table it was written for. A customers mapping in the leads dialog
   *  is not a near miss, it is a different set of field names entirely. */
  table: TransferTable;
  savedAt: string;
  /** Field to the file column it reads from. */
  fields: Record<string, string>;
  /** Field to a value put on every row. */
  constants: Record<string, string>;
}

export function mappingFileJson(
  table: TransferTable,
  fields: Record<string, string>,
  constants: Record<string, string>,
  now: Date = new Date(),
): string {
  const file: MappingFile = {
    kind: MAPPING_FILE_KIND,
    version: 1,
    table,
    savedAt: now.toISOString(),
    fields,
    constants,
  };
  // Indented on purpose. Somebody will open this in an editor to see what it
  // says, and one long line is the difference between a file they can read
  // and a file they have to take on trust.
  return JSON.stringify(file, null, 2) + '\n';
}

/**
 * Named after the file it was worked out from, so a folder of these says
 * which is which. Same rule as the not-imported export.
 */
export function mappingFileName(table: string, sourceFileName: string): string {
  const stem = sourceFileName.replace(/\.[^.]+$/, '').trim();
  const safe = stem.replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '');
  return `${safe || table}-mapping.json`;
}

/** Everything a load can turn out to be. A refusal names its reason; a load
 *  that dropped something says what and how much. */
export type MappingFileRead =
  | { ok: false; reason: string }
  | {
      ok: true;
      fields: Record<string, string>;
      constants: Record<string, string>;
      /** Fields this table no longer has. */
      unknownFields: string[];
      /** Columns the file in hand does not carry. */
      missingColumns: string[];
    };

/**
 * READ ONE, AGAINST THE FILE AND TABLE IN HAND.
 *
 * The account-side version could assume the shapes agreed: it was looked up
 * BY the file's own header signature, so a mapping that came back was one
 * written for a file like this one. A file on disk carries no such promise -
 * it can be for another table, or for a spreadsheet whose columns have since
 * been renamed - so what that lookup used to guarantee has to be checked
 * here, and what does not fit is dropped and counted rather than posted.
 */
export function readMappingFile(
  text: string,
  schema: TransferSchema,
  headers: readonly string[],
): MappingFileRead {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, reason: 'That file is not a saved mapping.' };
  }

  const file = parsed as Partial<MappingFile> | null;
  if (!file || typeof file !== 'object' || file.kind !== MAPPING_FILE_KIND)
    return { ok: false, reason: 'That file is not a saved mapping.' };

  if (file.table !== schema.table)
    return {
      ok: false,
      reason: `That mapping was saved for ${file.table ?? 'another table'},`
        + ` and this is ${schema.table}.`,
    };

  const known = new Set(schema.fields.map(field => field.name));
  const carried = new Set(headers);
  const fields: Record<string, string> = {};
  const unknownFields: string[] = [];
  const missingColumns: string[] = [];

  for (const [field, header] of Object.entries(readMap(file.fields))) {
    if (!known.has(field)) { unknownFields.push(field); continue; }
    // A column this file does not have would be posted as a mapping to
    // nothing, and the server would refuse every row for a reason the person
    // could not see on screen.
    if (!carried.has(header)) { missingColumns.push(header); continue; }
    fields[field] = header;
  }

  const constants: Record<string, string> = {};
  for (const [field, value] of Object.entries(readMap(file.constants))) {
    if (!known.has(field)) { unknownFields.push(field); continue; }
    constants[field] = value;
  }

  return { ok: true, fields, constants, unknownFields, missingColumns };
}

/** Whatever was in the file, reduced to the string-to-string map we can use.
 *  A hand-edited file is the normal case for something a person keeps. */
function readMap(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [key, held] of Object.entries(value as Record<string, unknown>))
    if (typeof held === 'string' && held.trim() !== '') out[key] = held;
  return out;
}

/**
 * What the load did, INCLUDING what it could not do.
 *
 * A load that silently dropped half a mapping would leave somebody looking at
 * a screen they think they recognise. The count and the column names are the
 * difference between "this is what you asked for" and "this is what of it
 * fits".
 */
export function loadedMappingNotice(
  fileName: string,
  read: Extract<MappingFileRead, { ok: true }>,
): string {
  const said = [`Loaded ${fileName}.`];

  if (read.missingColumns.length)
    said.push(
      (read.missingColumns.length === 1
        ? 'One column it names is'
        : `${read.missingColumns.length} columns it names are`)
      + ' not in this file, so '
      + (read.missingColumns.length === 1 ? 'it was' : 'they were')
      + ` left out: ${read.missingColumns.join(', ')}.`);

  if (read.unknownFields.length)
    said.push(
      (read.unknownFields.length === 1
        ? 'One field it names is'
        : `${read.unknownFields.length} fields it names are`)
      + ' no longer in this table.');

  return said.join(' ');
}
